import { Flex } from '../Flex';
import { Text } from '../Text';
import { useTheme } from '../ThemeProvider';
import { useFocusRing } from '../util/useFocusRing';

import React, { useMemo } from 'react';
import { Platform, Pressable, StyleSheet, type ViewStyle } from 'react-native';

export interface AlertAction {
    label: string;
    onPress: () => void;
}

export type AlertActionLayout = 'inline' | 'stacked';

interface AlertActionsProps {
    actions: AlertAction[];
    layout: AlertActionLayout;
}

function AlertActionButton({ label, onPress }: AlertAction) {
    const theme = useTheme();
    const { isFocusVisible, focusProps } = useFocusRing();

    return (
        <Pressable
            onPress={onPress}
            onFocus={focusProps.onFocus}
            onBlur={focusProps.onBlur}
            aria-label={label}
            accessibilityRole="button"
            style={[
                { alignItems: 'center', justifyContent: 'center' },
                Platform.OS === 'web' &&
                    isFocusVisible &&
                    ({
                        outlineStyle: 'solid',
                        outlineWidth: 2,
                        outlineColor: theme.colors.utilityFocus,
                        outlineOffset: 2,
                    } as ViewStyle),
            ]}
        >
            <Text
                level={3}
                sx={{ fontWeight: 'bold', color: theme.colors.foregroundActionStrong }}
            >
                {label}
            </Text>
        </Pressable>
    );
}

export function AlertActions({ actions, layout }: AlertActionsProps) {
    const theme = useTheme();
    const styles = useMemo(
        () =>
            StyleSheet.create({
                inlineContainer: {
                    flexDirection: 'row',
                    gap: theme.spacing.s3,
                    justifyContent: 'center',
                    paddingRight: theme.spacing.s2,
                },
                stackedContainer: {
                    alignItems: 'flex-start',
                    paddingTop: theme.spacing.s1,
                    gap: theme.spacing.s2,
                },
            }),
        [theme],
    );
    return (
        <Flex sx={layout === 'inline' ? styles.inlineContainer : styles.stackedContainer}>
            {actions.map(({ label, onPress }) => (
                <AlertActionButton key={`${label}-action`} label={label} onPress={onPress} />
            ))}
        </Flex>
    );
}
